import { Grid, Tabs, Typography } from '@mui/material';
import { BackButton } from '@tassat/button';
import { AssetTicker } from '@tassat/fred-extended-definitions';
import { HandshakeIcon, Lock as LockIcon, Wallet as WalletIcon } from '@tassat/icon';
import { TabPanel } from '@tassat/layout';
import { StyledTab } from '@tassat/menu';
import { AttributeTag, ClosedSymbol } from '@tassat/symbols';
import { WalletTotalBalanceTile } from '@tassat/text';
import React from 'react';

import Header from 'Containers/header';
import { getAccountBalancesTileData } from 'Lib/accountBalancesHelper';
import { hasApiCapabilities } from 'Lib/apiConnectionsHelper';
import history from 'Lib/browserHistory';
import { HANDSHAKE_ICON_COLOR, LOCK_ICON_COLOR } from 'Lib/constant';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountPositionsStatsByFirmId } from 'Selectors/accountPositions';
import { getSecurityCurrencyPairBySecurityTicker } from 'Selectors/assets';
import { getApiTypesByFirmId } from 'Selectors/firmApiTypes';
import { getFirmFromFirms } from 'Selectors/firms';

import FirmAccounts from './firmAccounts';
import FirmActivityLogs from './firmActivityLog';
import FirmDetails from './firmDetails/';
import FirmUsers from './firmUsers/';
import FirmWallets from './firmWallets/';
import FirmWithdrawl from './firmWithdrawl';

import {
    FIRM_VIEW_ACCOUNTS_TAB,
    FIRM_VIEW_ACTIVITY_TAB,
    FIRM_VIEW_DETAILS_TAB,
    FIRM_VIEW_USERS_TAB,
    FIRM_VIEW_WALLETS_TAB,
    FIRM_VIEW_WITHDRAWAL_INFO_TAB,
    IFirmViewProps
} from '.';

export function FirmView(props: IFirmViewProps) {
    const {
        classes,
        match,
        location
    } = props;

    const firmId = match.params.firmId;
    const firm = useAppSelector((state) => getFirmFromFirms(state, firmId));
    // TODO Review why assetPair is needed
    const assetPair = useAppSelector((state) => getSecurityCurrencyPairBySecurityTicker(state, AssetTicker.TFND));
    const accountPositionBalance = useAppSelector((state) => getAccountPositionsStatsByFirmId(state, firmId));
    const firmSecondaryApiConnections = useAppSelector((state) => getApiTypesByFirmId(state, firmId, false));

    React.useEffect(() => {
        if (!firm) {
            history.push('/admin/firms');
        }

        if (match.path.split('/')[4] === 'wallets'){
            setValue(FIRM_VIEW_WALLETS_TAB);
        }
    }, [ firm, match.path ]);

    const handleTabClick = (newValue: number) => {
        if (match.params.accountId || match.params.userId || match.params.accountWithdrawalDataId) {
            history.push(`/admin/firms/${firmId}`, { tab: newValue });
        }
        setValue(newValue);
    };

    const startingTab = React.useMemo(() => {
        if (match?.params.userId) {
            return FIRM_VIEW_USERS_TAB;
        }

        if (match?.params.accountWithdrawalDataId) {
            return FIRM_VIEW_WITHDRAWAL_INFO_TAB;
        }

        if (location?.state?.tab) {
            return location.state.tab;
        }

        return FIRM_VIEW_ACCOUNTS_TAB;
    }, [ match?.params, location?.state ]);

    const [ value, setValue ] = React.useState(startingTab);

    const accountPositionStats = React.useMemo(() => {
        if (Object.keys(accountPositionBalance[0].ccy).length === 0 && Object.keys(accountPositionBalance[0].security).length === 0) return;

        return getAccountBalancesTileData(accountPositionBalance);
    }, [accountPositionBalance]);

    const backButtonLink = () => {
        const tabSelected = match.path.split('/')[4];

        if (tabSelected && match.params.accountId) {
            return [
                {
                    to: '/admin/firms',
                    text: 'All Firms'
                },
                {
                    to: `/admin/firms/${firmId}`,
                    text: `All ${tabSelected}`,
                    handleNavigation: (url) => {
                        history.push(url, {
                            tab: match.path.includes('bdaccounts') ?
                                FIRM_VIEW_ACCOUNTS_TAB
                                : FIRM_VIEW_WALLETS_TAB
                        });
                    }
                }
            ];
        }

        return {
            to: '/admin/firms',
            text: 'All Firms'
        };
    };

    if (!firm) return <div />;

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Header />
            <Grid
                className={classes.backBar}
                item
                xs={12}
            >
                <BackButton
                    links={backButtonLink()}
                />
            </Grid>
            <Grid
                className={classes.topContent}
                container
                direction='row'
                item
                spacing={0}
                xs={12}
            >
                <Grid
                    item
                    xs={5}
                >
                    <Grid
                        alignItems='flex-start'
                        container
                        direction='column'
                        item
                        sm={12}
                        spacing={0}
                    >
                        <Typography
                            className={classes.itemTitle}
                            display='inline'
                            id='firmDetails_headerDetails_firmName_value'
                            variant='body2'
                        >
                            {firm.Name}
                        </Typography>
                        <Grid
                            alignItems='center'
                            container
                            direction='row'
                            justifyContent='flex-start'
                            spacing={0}
                            wrap='nowrap'
                        >
                            {firm.IsClosed && (
                                <ClosedSymbol
                                    className={classes.statusIcon}
                                    id='firmDetails_headerDetails_closedStatus'
                                />
                            )}
                            {
                                hasApiCapabilities({
                                    tokenApi: firm.IsTokenAPIFirm,
                                    secondaryApiList: firmSecondaryApiConnections
                                }) ?
                                    <AttributeTag
                                        attribute='API'
                                        className={classes.statusIcon}
                                        id='firmDetails_headerDetails_apiFirmStatus'
                                    />
                                    : null
                            }
                            {firm.IsLiquidityFacility && (
                                <AttributeTag
                                    attribute='LF'
                                    backgroundColor='#FDF9E2'
                                    borderColor='#EDCB26'
                                    className={classes.statusIcon}
                                    id='firmDetails_headerDetails_liquidityFacilityStatus'
                                />
                            )}
                            {firm.IsCollateral && (
                                <HandshakeIcon
                                    className={classes.statusIcon}
                                    iconColor={HANDSHAKE_ICON_COLOR}
                                    id='firmDetails_headerDetails_collateralStatus'
                                />
                            )}
                            {firm.IsLocked && (
                                <LockIcon
                                    className={classes.statusIcon}
                                    iconColor={LOCK_ICON_COLOR}
                                    id='firmDetails_headerDetails_lockedFirmStatus'
                                />
                            )}
                            {firm.IsFrozen && (
                                <WalletIcon
                                    className={classes.statusIcon}
                                    iconColor={LOCK_ICON_COLOR}
                                    id='firmDetails_headerDetails_frozenFirmStatus'
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction='column'
                    item
                    xs={7}
                >
                    {accountPositionStats && <WalletTotalBalanceTile stats={accountPositionStats} />}
                </Grid>
            </Grid>
            <Grid
                alignItems='center'
                className={classes.tabBar}
                container
                item
                justifyContent='center'
                spacing={0}
                xs={12}
            >
                <Tabs
                    aria-label='firm-tabs'
                    classes={{ indicator: classes.tabIndicator }}
                    textColor='secondary'
                    value={value}
                    variant='fullWidth'
                >
                    <StyledTab
                        classes={{ root: classes.tabRoot }}
                        id='firmBDAccountsTab'
                        label='Firm BD Accounts'
                        onClick={() => handleTabClick(FIRM_VIEW_ACCOUNTS_TAB)}
                        style='basicFontThin'
                    />
                    <StyledTab
                        classes={{ root: classes.tabRoot }}
                        id='firmAccountsTab'
                        label='Firm Accounts'
                        onClick={() => handleTabClick(FIRM_VIEW_WALLETS_TAB)}
                        style='basicFontThin'
                    />
                    <StyledTab
                        classes={{ root: classes.tabRoot }}
                        id='firmDetailsTab'
                        label='Firm Details'
                        onClick={() => handleTabClick(FIRM_VIEW_DETAILS_TAB)}
                        style='basicFontThin'
                    />
                    <StyledTab
                        classes={{ root: classes.tabRoot }}
                        id='firmUsersTab'
                        label='Users'
                        onClick={() => handleTabClick(FIRM_VIEW_USERS_TAB)}
                        style='basicFontThin'
                    />
                    <StyledTab
                        classes={{ root: classes.tabRoot }}
                        id='firmActivityLogTab'
                        label='Activity Log'
                        onClick={() => handleTabClick(FIRM_VIEW_ACTIVITY_TAB)}
                        style='basicFontThin'
                    />
                    <StyledTab
                        classes={{ root: classes.tabRoot }}
                        id='firmWithdrawalInformationTab'
                        label='Withdrawal Information'
                        onClick={() => handleTabClick(FIRM_VIEW_WITHDRAWAL_INFO_TAB)}
                        style='basicFontThin'
                    />
                </Tabs>
            </Grid>
            <Grid
                className={classes.tabContent}
                container
                item
                spacing={0}
                xs={12}
            >
                <TabPanel
                    className={classes.tabPanel}
                    index={FIRM_VIEW_ACCOUNTS_TAB}
                    value={value}
                >
                    <FirmAccounts firm={firm} />
                </TabPanel>
                <TabPanel
                    className={classes.tabPanel}
                    index={FIRM_VIEW_WALLETS_TAB}
                    value={value}
                >
                    <FirmWallets
                        assetPair={assetPair}
                        firm={firm}
                    />
                </TabPanel>
                <TabPanel
                    className={classes.tabPanel}
                    index={FIRM_VIEW_DETAILS_TAB}
                    value={value}
                >
                    <FirmDetails
                        assetPair={assetPair}
                        firmId={firmId}
                    />
                </TabPanel>
                <TabPanel
                    className={classes.tabPanel}
                    index={FIRM_VIEW_USERS_TAB}
                    value={value}
                >
                    <FirmUsers
                        firm={firm}
                        sortModel={location.state?.sortModel}
                    />
                </TabPanel>
                <TabPanel
                    className={classes.tabPanel}
                    index={FIRM_VIEW_ACTIVITY_TAB}
                    value={value}
                >
                    <FirmActivityLogs firm={firm} />
                </TabPanel>
                <TabPanel
                    className={classes.tabPanel}
                    index={FIRM_VIEW_WITHDRAWAL_INFO_TAB}
                    value={value}
                >
                    <FirmWithdrawl
                        firm={firm}
                    />
                </TabPanel>
            </Grid>
        </Grid>
    );
}